import Login from '@mui/icons-material/Login';
import { Box, Button, Stack, SxProps, Typography } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Utils/Card';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { post, baseApi } from '../lib/api';
import { localAuth, loginRequest, logoutWithoutRedirect } from '../lib/api/auth';

import { ReactComponent as LogoTP } from './logo_tp.svg';
import { ReactComponent as Logo } from '../components/Layout/logo.svg';

import { Theme } from '@mui/system';
import Link from '../components/Utils/Link';
import { getEnv } from '../lib/helper/get-env';
import { useEventListener } from 'usehooks-ts';
import { useScreenSmallerThan } from '../lib/hooks/breakpoint-hooks';
import appInsights from '../lib/analytics/app-insights';

const LoginPage = () => {
    const [isNoRole, setIsNoRole] = useState(false);
    const isSmallerThanLarge = useScreenSmallerThan('lg');

    const isAuthenticated = useIsAuthenticated();
    const nav = useNavigate();

    const { instance, accounts } = useMsal();

    const submitHandler = async (e: FormEvent) => {
        e.preventDefault();
        instance.loginRedirect(loginRequest);
    };

    const redirectToLogin = ({ key }: KeyboardEvent) => {
        if (key === 'Enter') instance.loginRedirect(loginRequest);
    };
    useEventListener('keydown', redirectToLogin);

    useEffect(() => {
        const handleAuthResult = async () => {
            const authResult = await instance.handleRedirectPromise();
            if (!authResult) return;

            const { accessToken, account } = authResult;

            if (account) {
                const roleScope = await getEnv('ROLE_SCOPE', true);
                const role = account.idTokenClaims?.roles;

                const isInRoleScope = roleScope?.split(',').some((rs) => role?.some((r: string) => r !== rs));

                if (!isInRoleScope) {
                    setIsNoRole(true);
                } else {
                    instance.setActiveAccount(account);
                    baseApi.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
                }
            }
        };
        handleAuthResult();
    }, [accounts, instance, nav]);

    useEffect(() => {
        if (!isNoRole && isAuthenticated && accounts.length > 0) {
            const account = accounts[0];
            appInsights.setAuthenticatedUserContext(account.username, account.localAccountId);
            post('user/init-user').catch((ex) => {
                console.error('failed checking user profile on login', ex);
            });
        }
    }, [isNoRole, isAuthenticated, accounts]);

    useEffect(() => {
        const handlePostAuthResult = async () => {
            if (isNoRole) {
                await logoutWithoutRedirect(instance);
                return;
            }

            if (isAuthenticated && accounts.length > 0) {
                nav('/');
                localAuth.login();
            } else {
                localAuth.logout();
            }
        };
        handlePostAuthResult();
    }, [instance, isAuthenticated, accounts, nav, isNoRole]);

    const errorStyle: SxProps<Theme> = {
        color: (t) => t.palette.error.main,
        fontWeight: 'medium',
        fontSize: 14
    };

    return (
        <Stack
            sx={{
                height: '100vh',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
            <Stack sx={{ gap: 2, marginX: isSmallerThanLarge ? 3 : 0 }}>
                <Box sx={{ textAlign: 'center', paddingBottom: 2 }}>
                    <Logo width={'90%'} height={100} />
                </Box>
                {isNoRole && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={errorStyle}>Your account is not registered,</Typography>
                        <Typography sx={errorStyle}>
                            please contact{' '}
                            <Link
                                sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                                to={'/support'}>
                                Support
                            </Link>
                        </Typography>
                    </Box>
                )}
                <Card>
                    <Card.Title>
                        <Stack direction={'row'} alignItems="center" gap={1}>
                            <Login fontSize={'small'} /> Login
                        </Stack>
                    </Card.Title>
                    <Card.SubTitle sx={{ fontSize: 12 }}>
                        Use your TurningPoint account to use MD Navigator
                    </Card.SubTitle>
                    <Card.Body>
                        <Stack paddingTop={0} direction="column" marginTop={3} gap={1}>
                            <Button
                                sx={{
                                    backgroundColor: (t) => t.palette.secondary.main,
                                    borderRadius: '0.313rem'
                                }}
                                onClick={submitHandler}
                                variant="contained">
                                <Stack direction={'row'} alignItems="center" gap={1}>
                                    <Typography
                                        sx={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
                                        Login with your
                                    </Typography>
                                    <LogoTP width={24} />
                                    <Typography
                                        sx={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
                                        Account
                                    </Typography>
                                </Stack>
                            </Button>
                        </Stack>
                    </Card.Body>
                </Card>
                <Stack sx={{ alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white' }}>
                        Having trouble logging in?
                    </Typography>
                    <Link to="/support" sx={{ fontWeight: 700, fontSize: 16 }}>
                        Contact Support
                    </Link>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default LoginPage;
